import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, toast } from '../App';
import { Plus, RefreshCw, Trash2, FileText } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import ServiceLLMToggle from '../components/ServiceLLMToggle';

// Import sub-components from extracted modules
import { STATUS_COLOR } from '../components/AutoDossier/constants';
import CreateDossier from '../components/AutoDossier/CreateDossier';
import EditDossier from '../components/AutoDossier/EditDossier';

/**
 * AutoDossier - Orchestrateur SEO multisite
 * Gère la liste des dossiers et la navigation vers Create/Edit
 */
const AutoDossier = () => {
  const [view, setView] = useState('list'); // list | create | edit
  const [dossiers, setDossiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [eligibleSites, setEligibleSites] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [deleteModal, setDeleteModal] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/auto-dossiers`);
      setDossiers(data.dossiers || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadEligible = async () => {
    try {
      const { data } = await axios.get(`${API}/auto-dossiers/eligible-sites`);
      setEligibleSites(data.sites || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur sites éligibles');
    }
  };

  useEffect(() => { loadList(); }, [loadList]);

  const openCreate = async () => {
    await loadEligible();
    setView('create');
  };

  const openEdit = async (id) => {
    await loadEligible();
    setEditingId(id);
    setView('edit');
  };

  const removeDossier = (id) => {
    const target = dossiers.find(d => d.id === id);
    setDeleteModal({ id, title: target?.title || target?.dossier_title || '(sans titre)' });
  };

  const confirmDelete = async () => {
    if (!deleteModal?.id) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/auto-dossiers/${deleteModal.id}`);
      toast.success('Dossier supprimé');
      setDeleteModal(null);
      loadList();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Suppression échouée');
    } finally {
      setDeleting(false);
    }
  };

  // Route to Create view
  if (view === 'create') {
    return (
      <CreateDossier
        eligibleSites={eligibleSites}
        onCancel={() => setView('list')}
        onCreated={(id) => { setEditingId(id); setView('edit'); loadList(); }}
      />
    );
  }

  // Route to Edit view
  if (view === 'edit' && editingId) {
    return (
      <EditDossier
        dossierId={editingId}
        eligibleSites={eligibleSites}
        onBack={() => { setView('list'); loadList(); }}
      />
    );
  }

  // Main list view
  return (
    <div className="min-h-screen p-4 lg:p-8" data-testid="auto-dossier-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-sky-300 font-rajdhani uppercase tracking-wider">Auto-dossier</h1>
            <p className="text-sm text-slate-400 mt-1">Orchestrateur SEO multisite — grappes d&apos;articles autour d&apos;un pilier</p>
          </div>
          <div className="flex gap-2 items-center">
            <ServiceLLMToggle serviceId="dossier" />
            <Button onClick={loadList} variant="outline" size="sm" data-testid="refresh-list-btn">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Rafraîchir
            </Button>
            <Button onClick={openCreate} className="bg-sky-600 hover:bg-sky-500" data-testid="new-dossier-btn">
              <Plus className="w-4 h-4 mr-2" /> Nouveau dossier
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12 text-slate-400">Chargement…</div>
        ) : dossiers.length === 0 ? (
          <Card className="border-sky-500/30 bg-slate-900/40">
            <CardContent className="py-12 text-center">
              <FileText className="w-12 h-12 text-slate-500 mx-auto mb-3" />
              <p className="text-slate-400 mb-4">Aucun dossier pour le moment.</p>
              <Button onClick={openCreate} className="bg-sky-600 hover:bg-sky-500" data-testid="create-first-dossier-btn">
                <Plus className="w-4 h-4 mr-2" /> Créer mon premier dossier
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-3" data-testid="dossiers-list">
            {dossiers.map(d => (
              <Card key={d.id} className="border-sky-500/30 bg-slate-900/40 hover:border-sky-400/60 transition" data-testid={`dossier-card-${d.id}`}>
                <CardContent className="p-4 flex items-center justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-slate-100 truncate">{d.title || d.dossier_title || 'Sans titre'}</h3>
                      <Badge className={`text-[10px] border ${STATUS_COLOR[d.status] || STATUS_COLOR.draft}`}>{d.status}</Badge>
                    </div>
                    <p className="text-xs text-slate-400 truncate">
                      <span className="text-sky-400">{d.main_keyword}</span>
                      {d.objective ? ` · ${d.objective}` : ''}
                      {` · ${(d.selected_site_ids || []).length} site(s)`}
                    </p>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <Button onClick={() => openEdit(d.id)} variant="outline" size="sm" data-testid={`open-dossier-${d.id}`}>Ouvrir</Button>
                    <Button onClick={() => removeDossier(d.id)} variant="outline" size="sm" className="text-red-400 hover:text-red-300" data-testid={`delete-dossier-${d.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Delete confirmation modal */}
      {deleteModal && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => !deleting && setDeleteModal(null)}
          data-testid="delete-dossier-modal"
        >
          <Card className="border-red-500/50 bg-slate-900 max-w-md w-full" onClick={e => e.stopPropagation()}>
            <CardHeader>
              <CardTitle className="text-sm text-red-300 flex items-center gap-2">
                <Trash2 className="w-4 h-4" /> Supprimer ce dossier ?
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-slate-300">
                Vous êtes sur le point de supprimer définitivement le dossier
                <span className="text-slate-100 font-semibold"> « {deleteModal.title} »</span>.
              </p>
              <p className="text-xs text-slate-500">
                Tous les items et liens associés seront également supprimés. Cette action est irréversible.
              </p>
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  onClick={() => setDeleteModal(null)}
                  variant="outline"
                  size="sm"
                  disabled={deleting}
                  data-testid="delete-dossier-cancel-btn"
                >
                  Annuler
                </Button>
                <Button
                  onClick={confirmDelete}
                  disabled={deleting}
                  className="bg-red-600 hover:bg-red-500"
                  size="sm"
                  data-testid="delete-dossier-confirm-btn"
                >
                  <Trash2 className="w-3 h-3 mr-1" />
                  {deleting ? 'Suppression…' : 'Supprimer définitivement'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AutoDossier;
